// src/components/common/InactivityWarningModal.tsx

import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Space } from 'antd';
import { ClockCircleOutlined, LogoutOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface InactivityWarningModalProps {
  visible: boolean;
  onContinue: () => void;
  onLogout: () => void;
  countdownSeconds: number;
}

/**
 * Modal component that warns users about upcoming auto-logout
 * Shows a countdown timer and allows users to continue their session
 */
export const InactivityWarningModal: React.FC<InactivityWarningModalProps> = ({
  visible,
  onContinue,
  onLogout,
  countdownSeconds,
}) => {
  const [timeLeft, setTimeLeft] = useState(countdownSeconds);

  // Update countdown every second
  useEffect(() => {
    if (!visible) return;

    setTimeLeft(countdownSeconds);
    
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Auto-logout when countdown reaches zero
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [visible, countdownSeconds, onLogout]);

  // Reset countdown when modal becomes visible
  useEffect(() => {
    if (visible) {
      setTimeLeft(countdownSeconds);
    }
  }, [visible, countdownSeconds]);

  const handleContinue = () => {
    onContinue();
  };

  const handleLogout = () => {
    onLogout();
  };

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined style={{ color: '#faad14' }} />
          <span>Session expirée</span>
        </Space>
      }
      open={visible}
      closable={false}
      maskClosable={false}
      footer={null}
      centered
      width={400}
      style={{
        top: '20%',
      }}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          Votre session va expirer
        </Title>
        
        <div style={{ marginBottom: 24 }}>
          <Text type="secondary" style={{ fontSize: 16 }}>
            Vous avez été inactif pendant plusieurs minutes. 
            Votre session se terminera automatiquement dans :
          </Text>
        </div>

        <div style={{ marginBottom: 32 }}>
          <Title 
            level={1} 
            style={{ 
              color: timeLeft <= 10 ? '#ff4d4f' : '#faad14',
              fontSize: 48,
              margin: 0,
              fontFamily: 'monospace'
            }}
          >
            {timeLeft}
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            seconde{timeLeft > 1 ? 's' : ''}
          </Text>
        </div>

        <Space size="middle">
          <Button
            type="primary"
            size="large"
            onClick={handleContinue}
            style={{
              backgroundColor: '#4ade80',
              borderColor: '#4ade80',
              minWidth: 120,
            }}
          >
            Continuer la session
          </Button>
          
          <Button
            size="large"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              minWidth: 120,
            }}
          >
            Se déconnecter
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default InactivityWarningModal;
